<template>
  <AnnDialog
    :model-value="props.modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <div class="ann-flame-supply">
      <img
        class="ann-flame-supply-bg"
        :src="backgroundImg"
        alt=""
        aria-hidden="true"
      />
      <div class="ann-flame-supply-content-bg"></div>

      <div class="ann-flame-supply-title">
        <img
          :class="`is-${titleLang}`"
          :src="titleImg"
          :alt="t('ann2026.flameSupply.title')"
        />
      </div>
      <div class="ann-flame-supply-subtitle">{{ t('ann2026.flameSupply.subtitle') }}</div>

      <div class="ann-flame-supply-scroll">
        <div
          v-for="task in props.tasks"
          :key="task.taskType"
          class="ann-flame-supply-row"
        >
          <img
            class="ann-flame-supply-row-bg"
            :src="rowBackgroundImg"
            alt=""
            aria-hidden="true"
          />
          <div class="ann-flame-supply-task-name">
            <span>{{ taskName(task) }}</span>
            <span
              v-if="isShareTask(task.taskType)"
              class="ann-flame-supply-share-link"
              @click.stop="emit('share')"
            >【{{ t('mwr.mission.btns.shareImmediate') }}】</span>
          </div>
          <img
            class="ann-flame-supply-fire-icon"
            :src="fireIconImg"
            alt=""
            aria-hidden="true"
          />
          <div class="ann-flame-supply-fire-count">
            +{{ task.fireCount ?? 0 }}
          </div>
          <button
            class="ann-flame-supply-status"
            :class="{
              'is-completed': task.status === 1,
              'is-action': task.status === 1,
            }"
            type="button"
            :disabled="task.status !== 1"
            @click="handleTask(task)"
          >
            <img
              :src="task.status === 1 ? buttonActiveImg : buttonDisabledImg"
              alt=""
              aria-hidden="true"
            />
            <span>{{ statusText(task.status) }}</span>
          </button>
        </div>
      </div>

      <img
        class="ann-flame-supply-bow"
        :src="bowImg"
        alt=""
        aria-hidden="true"
      />

      <AnnDialogClose
        :aria-label="t('ann2026.dialog.close')"
        @close="emit('update:modelValue', false)"
      />
    </div>
  </AnnDialog>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import AnnDialog from '../AnnDialog/index.vue'
import AnnDialogClose from '../AnnDialogClose/index.vue'
import backgroundImg from '@/assets/imgs/act/ann2026/popups/flame-supply/background.png'
import rowBackgroundImg from '@/assets/imgs/act/ann2026/popups/flame-supply/task-row.png'
import fireIconImg from '@/assets/imgs/act/ann2026/popups/flame-supply/flame-icon.png'
import buttonDisabledImg from '@/assets/imgs/act/ann2026/popups/flame-supply/button-disabled.png'
import buttonActiveImg from '@/assets/imgs/act/ann2026/popups/flame-supply/button-active.png'
import bowImg from '@/assets/imgs/act/ann2026/popups/flame-supply/bow.png'
import titleEn from '@/assets/imgs/act/ann2026/popups/flame-supply/title/en.png'
import titleZh from '@/assets/imgs/act/ann2026/popups/flame-supply/title/zh.png'
import titleVi from '@/assets/imgs/act/ann2026/popups/flame-supply/title/vi.png'
import titleId from '@/assets/imgs/act/ann2026/popups/flame-supply/title/id.png'
import titleTh from '@/assets/imgs/act/ann2026/popups/flame-supply/title/th.png'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  tasks: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'share', 'receive'])

const { t, te, locale } = useI18n({ useScope: 'global' })

const titleLang = computed(() => {
  if (locale.value === 'vi') return 'vi'
  if (locale.value === 'id') return 'id'
  if (locale.value === 'th') return 'th'
  if (locale.value === 'hk' || locale.value === 'tw' || locale.value === 'cn') {
    return 'zh'
  }
  return 'en'
})

const titleImg = computed(() => {
  if (titleLang.value === 'vi') return titleVi
  if (titleLang.value === 'id') return titleId
  if (titleLang.value === 'th') return titleTh
  if (titleLang.value === 'zh') return titleZh
  return titleEn
})

const taskName = (task) => {
  const keys = [
    String(task.nameKey || '').startsWith('ann2026.') ? task.nameKey : null,
    `ann2026.task.${task.taskType}`,
  ].filter(Boolean)
  const key = keys.find((item) => te(item))
  const name = key ? t(key) : task.taskName || task.taskType
  const labelKey = task.isDaily
    ? 'ann2026.task.dailyLabel'
    : 'ann2026.task.weeklyLabel'
  const label = te(labelKey) ? t(labelKey) : ''
  return label ? `${name} (${label})` : name
}

const statusText = (status) => {
  if (status === 2) return t('ann2026.flameSupply.claimed')
  if (status === 1) return t('ann2026.flameSupply.completed')
  return t('ann2026.flameSupply.incomplete')
}

const isShareTask = (taskType) => String(taskType || '') === 'ann_weekly_share'

const handleTask = (task) => {
  if (task.status === 1) {
    emit('receive', task.taskType)
  }
}
</script>

<style lang="less" scoped>
.ann-flame-supply {
  position: relative;
  width: 701px;
  height: 647px;
  overflow: visible;
}

.ann-flame-supply-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 701px;
  height: 651px;
  pointer-events: none;
}

.ann-flame-supply-content-bg {
  position: absolute;
  top: 104px;
  left: 52px;
  z-index: 1;
  width: 597px;
  height: 486px;
  border-radius: 42px;
  background: #fdfbfe;
  pointer-events: none;
}

.ann-flame-supply-title {
  position: absolute;
  // 设计意图：标题向上探出弹窗顶部边缘。
  top: -10px;
  left: 0;
  z-index: 3;
  width: 701px;
  display: flex;
  justify-content: center;

  img {
    display: block;
    // 图片标题锁宽跟随缩放，height auto 保持各语言切图等比不变形。
    height: auto;
    object-fit: contain;

    &.is-en {
      width: 450px;
    }

    &.is-zh {
      width: 322px;
    }

    &.is-vi {
      width: 616px;
    }

    &.is-id {
      width: 642px;
    }

    &.is-th {
      width: 329px;
    }
  }
}

.ann-flame-supply-subtitle {
  position: absolute;
  top: 130px;
  left: 159px;
  z-index: 2;
  width: 386px;
  height: 20px;
  color: #273271;
  font-size: 18px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

.ann-flame-supply-scroll {
  position: absolute;
  top: 178px;
  left: 64px;
  z-index: 2;
  width: 576px;
  min-width: 576px;
  max-width: 576px;
  height: 374px;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.ann-flame-supply-row {
  position: relative;
  width: 576px;
  min-width: 576px;
  max-width: 576px;
  height: 75px;
  margin-bottom: 24.33px;
  flex: none;

  &:last-child {
    margin-bottom: 0;
  }
}

.ann-flame-supply-row-bg {
  position: absolute;
  inset: 0;
  width: 576px;
  height: 75px;
}

.ann-flame-supply-task-name {
  position: absolute;
  // top: 32px;
  top: 50%;
  transform: translateY(-50%);
  left: 30px;
  width: 320px;
  overflow: hidden;
  color: #fff;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  // text-overflow: ellipsis;
  // white-space: nowrap;
}

.ann-flame-supply-share-link {
  color: #fff100;
  cursor: pointer;
  white-space: nowrap;
}

.ann-flame-supply-fire-icon {
  position: absolute;
  top: 5px;
  left: 364px;
  width: 17px;
  height: 61px;
}

.ann-flame-supply-fire-count {
  position: absolute;
  top: 31px;
  left: 385px;
  width: 31px;
  height: 20px;
  color: #fff;
  font-size: 17px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

.ann-flame-supply-status {
  position: absolute;
  top: 16.5px;
  left: 428px;
  width: 132px;
  height: 42.1px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 1;
  }

  &.is-action {
    cursor: pointer;
  }

  img {
    position: absolute;
    inset: 0;
    width: 132px;
    height: 42.1px;
  }

  span {
    display: -webkit-box;
    width: 100%;
    max-height: 32px;
    padding: 0 10px;
    box-sizing: border-box;
    // position: relative;
    z-index: 2;
    // top: 14.5px;
    // left: 16px;
    // width: 87px;

    // overflow: hidden;
    overflow: hidden;
    color: #fff;
    font-size: 14px;
    line-height: 16px;
    font-weight: 700;
    text-align: center;
    // text-overflow: ellipsis;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &.is-completed span {
    color: #17236c;
  }
}

.ann-flame-supply-bow {
  position: absolute;
  top: 592px;
  left: 272px;
  z-index: 4;
  width: 156px;
  height: 119px;
  pointer-events: none;
}
</style>
